import React, { useState, useEffect, useRef } from "react";
import "../styles/StoryReader.css";
import { storyContent, balladLyrics } from "../data/graengaleGroveStory";
import {
  StorySection,
  parseMarkdown,
  renderMarkdownToHTML,
} from "../utils/storyHelpers";

interface CharacterInfo {
  name: string;
  description: string;
  image: string;
}

const StoryReader: React.FC = () => {
  const [sections, setSections] = useState<StorySection[]>([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [darkMode, setDarkMode] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedCharacter, setSelectedCharacter] =
    useState<CharacterInfo | null>(null);
  const [hasFinishedReading, setHasFinishedReading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const characters: { [key: string]: CharacterInfo } = {
    Valora: {
      name: "Valora Elderleaf",
      description:
        "A curious wood elf who ventured into the forbidden Graengale Grove, forever changed by her journey between worlds.",
      image: require("../assets/Stories/Graengale Grove/ValoraElderleaf.png"),
    },
    Elderleaf: {
      name: "Valora Elderleaf",
      description:
        "A curious wood elf who ventured into the forbidden Graengale Grove, forever changed by her journey between worlds.",
      image: require("../assets/Stories/Graengale Grove/ValoraElderleaf.png"),
    },
    Filivindabair: {
      name: "Filivindabair the Fair",
      description:
        "Mother of Nature, one of the three ancient elven deities who tend to the World Tree in Kai'denn.",
      image: require("../assets/Stories/Graengale Grove/Filivindabair.png"),
    },
    Milihaniel: {
      name: "Milihaniel the Wise",
      description:
        "Father of Magic, an ancient elven deity whose power flows through the ethereal realm of Kai'denn.",
      image: require("../assets/Stories/Graengale Grove/Milihaniel.png"),
    },
    Istellinith: {
      name: "Istellinith the Strong",
      description:
        "Father of the Land, an ancient elven deity who embodies the strength and endurance of the earth itself.",
      image: require("../assets/Stories/Graengale Grove/Istellinith.png"),
    },
  };

  useEffect(() => {
    const parsedSections = parseMarkdown(storyContent);

    if (hasFinishedReading) {
      try {
        const balladGif = require("../assets/Stories/Graengale Grove/Ballad.gif");
        const balladAudio = require("../assets/Stories/Graengale Grove/The Ballad of Graengale Grove.mp3");

        parsedSections.push({
          title: "Ballad of Graengale Grove",
          content: balladLyrics,
          audioFile: balladAudio,
          isSpecial: true,
          gifUrl: balladGif,
        });
      } catch (error) {
        console.error("Error loading ballad:", error);
      }
    }

    setSections(parsedSections);
  }, [hasFinishedReading]);

  useEffect(() => {
    if (audioRef.current && sections.length > 0) {
      audioRef.current.volume = isMuted ? 0 : volume;
      audioRef.current
        .play()
        .catch((e) => console.log("Autoplay prevented:", e));
    }
  }, [currentSection, sections, volume, isMuted]);

  useEffect(() => {
    const mainStoryLength = 7;
    if (currentSection >= mainStoryLength - 1 && sections.length > 0) {
      setHasFinishedReading(true);
    }
  }, [currentSection, sections.length]);

  useEffect(() => {
    let isScrolling = false;
    let scrollAttempts = 0;
    let lastScrollTime = 0;

    const handleWheel = (e: WheelEvent) => {
      if (isScrolling) return;

      const now = Date.now();
      const timeSinceLastScroll = now - lastScrollTime;

      if (timeSinceLastScroll > 1000) {
        scrollAttempts = 0;
      }

      lastScrollTime = now;

      const atBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;
      const atTop = window.scrollY <= 100;

      if (e.deltaY > 0 && atBottom && currentSection < sections.length - 1) {
        scrollAttempts++;
        if (scrollAttempts >= 3) {
          isScrolling = true;
          scrollAttempts = 0;
          setCurrentSection(currentSection + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
          setTimeout(() => {
            isScrolling = false;
          }, 1000);
        }
      } else if (e.deltaY < 0 && atTop && currentSection > 0) {
        scrollAttempts++;
        if (scrollAttempts >= 3) {
          isScrolling = true;
          scrollAttempts = 0;
          setCurrentSection(currentSection - 1);
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
          setTimeout(() => {
            isScrolling = false;
          }, 1000);
        }
      } else {
        scrollAttempts = 0;
      }
    };

    window.addEventListener("wheel", handleWheel);
    return () => window.removeEventListener("wheel", handleWheel);
  }, [currentSection, sections.length]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const handleCharacterClick = (characterKey: string) => {
    const character = characters[characterKey];
    if (character) {
      if (selectedCharacter?.name === character.name) {
        setSelectedCharacter(null);
      } else {
        setSelectedCharacter(character);
      }
    }
  };

  const renderContentWithHoverables = (content: string) => {
    let result = renderMarkdownToHTML(content);

    Object.keys(characters).forEach((key) => {
      const regex = new RegExp(`\\b${key}\\b`, "g");
      result = result.replace(
        regex,
        `<span class="hoverable-character" data-character="${key}">${key}</span>`
      );
    });

    return result;
  };

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains("hoverable-character")) {
      const characterKey = target.getAttribute("data-character");
      if (characterKey) {
        handleCharacterClick(characterKey);
      }
    } else if (!target.closest(".character-card-content")) {
      setSelectedCharacter(null);
    }
  };

  if (sections.length === 0) {
    return <div className="story-reader loading">Loading story...</div>;
  }

  const currentSectionData = sections[currentSection];

  return (
    <div className={`story-reader ${darkMode ? "dark-mode" : "light-mode"}`}>
      <div className="orb-controls">
        <button
          className="orb mode-orb"
          onClick={() => setDarkMode(!darkMode)}
          title="Toggle theme"
        >
          {darkMode ? "◐" : "◑"}
        </button>
        <button
          className="orb mute-orb"
          onClick={toggleMute}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? "🔇" : "🔊"}
        </button>
        <div className="orb volume-orb">
          <span className="volume-icon">♪</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
        </div>
      </div>

      <nav className="chapter-nav">
        {sections.map((section, index) => (
          <button
            key={index}
            className={`chapter-dot ${
              index === currentSection ? "active" : ""
            }`}
            onClick={() => setCurrentSection(index)}
            title={section.title}
          >
            <span className="dot-label">{section.title}</span>
          </button>
        ))}
      </nav>

      <main className="story-main">
        <article className="story-article">
          <header className="story-header-minimal">
            <h1 className="story-title-minimal">Graengale Grove</h1>
            <h2 className="chapter-title">{currentSectionData.title}</h2>
          </header>

          {currentSectionData.gifUrl && (
            <div className="ballad-gif">
              <img src={currentSectionData.gifUrl} alt="Ballad animation" />
            </div>
          )}

          <div
            className="story-prose"
            dangerouslySetInnerHTML={{
              __html: renderContentWithHoverables(currentSectionData.content),
            }}
            onClick={handleContentClick}
          />
        </article>
      </main>

      <audio ref={audioRef} src={currentSectionData.audioFile} loop autoPlay />

      {selectedCharacter && (
        <aside className="character-card-modal" onClick={handleContentClick}>
          <div
            className="character-card-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedCharacter.image}
              alt={selectedCharacter.name}
              className="character-image"
            />
            <h3>{selectedCharacter.name}</h3>
            <p>{selectedCharacter.description}</p>
            <button
              className="close-card"
              onClick={() => setSelectedCharacter(null)}
            >
              ✕
            </button>
          </div>
        </aside>
      )}
    </div>
  );
};

export default StoryReader;
